//this main(1st) Blogmanagement

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./BlogManagement.css";
import { saveBlogPosts } from "../utils/blogPostsStorage";
import {
  getBlogs,
  createBlog,
  updateBlog,
  getImages,
} from "../services/blogService";

const isVideoUrl = (url = "") => /\.(mp4|webm|ogg)(?:$|\?)/i.test(url);

const getVideoMimeType = (url = "") => {
  if (/\.webm(?:$|\?)/i.test(url)) return "video/webm";
  if (/\.ogg(?:$|\?)/i.test(url)) return "video/ogg";
  return "video/mp4";
};

const emptyForm = {
  title: "",
  content: "",
  status: "DRAFT",
  meta_title: "",
  meta_description: "",
  featured_image: [],
};

const BlogManagement = () => {
  const adminBasePath = "/components/Admins/AdminLayout";
  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [availableImages, setAvailableImages] = useState([]);
  const [selectedDatabaseImages, setSelectedDatabaseImages] = useState([]);
  const [showImageDropdown, setShowImageDropdown] = useState(false);
  const [totalPosts, setTotalPosts] = useState(0);

  const [errors, setErrors] = useState({}); // ✅ NEW

  /* ============================
     VALIDATION FUNCTION
  ============================ */

  const validateForm = () => {
    const newErrors = {};

    // TITLE
    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    } else if (formData.title.length < 3 || formData.title.length > 100) {
      newErrors.title = "Title must be between 3 and 100 characters";
    }

    // CONTENT
    if (!formData.content.trim()) {
      newErrors.content = "Content is required";
    } else if (formData.content.length < 100) {
      newErrors.content = "Content must be at least 100 characters";
    }

    // STATUS
    if (!["DRAFT", "PUBLISHED"].includes(formData.status)) {
      newErrors.status = "Status must be DRAFT or PUBLISHED";
    }

    // FEATURED IMAGE (validate each URL)
    if (formData.featured_image?.length > 0) {
      const invalid = formData.featured_image.find(
        (url) => !/^https?:\/\/.+/.test(url)
      );
      if (invalid) {
        newErrors.featured_image = "Featured image must be a valid URL";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ============================
     SUMMARY LABEL
  ============================ */

  const summaryLabel = useMemo(() => {
      const publishedCount = posts.filter(
        (post) => post.status === "PUBLISHED"
      ).length;
  
      return `${totalPosts} posts | ${publishedCount} published`;
    }, [posts, totalPosts]);

  /* ============================
     FETCH BLOGS
  ============================ */

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const res = await getBlogs();

      console.log("Blogs API Response:", res);

      const payload = res?.data ?? res ?? {};
      let blogs = [];

      if (Array.isArray(payload)) {
        blogs = payload;
      } else if (Array.isArray(payload?.blogs)) {
        blogs = payload.blogs;
      } else if (Array.isArray(payload?.content)) {
        blogs = payload.content;
      }

      setPosts(blogs);
      setTotalPosts(payload?.total || blogs.length);
    } catch (err) {
      console.error("Fetch blogs failed:", err);
      setPosts([]);
      setTotalPosts(0);
    }
  };

  /* ============================
     FETCH IMAGES
  ============================ */

  const fetchImages = async () => {
    try {
      const res = await getImages();

      console.log("Images API Response:", res);

      let images = [];

      if (Array.isArray(res)) images = res;
      else if (Array.isArray(res?.data)) images = res.data;

      const formattedImages = images
        .map((img, index) => ({
          id: img.id || img._id || index,
          name: img.name || img.filename || `Image ${index + 1}`,
          url:
            img.url ||
            img.fileUrl ||
            img.imageUrl ||
            img.secure_url ||
            img.src ||
            "",
        }))
        .filter((image) => image.url);

      setAvailableImages(formattedImages);
      setShowImageDropdown(true);
    } catch (err) {
      console.error("Fetch images failed:", err);
    }
  };

  const handleDatabaseImageSelect = (event) => {
    const values = Array.from(
      event.target.selectedOptions,
      (option) => option.value
    );
    setSelectedDatabaseImages(values);
  };

  useEffect(() => {
    saveBlogPosts(posts);
  }, [posts]);

  /* ============================
     RESET FORM
  ============================ */

  const resetForm = () => {
    setFormData(emptyForm);
    setErrors({});
  };

  /* ============================
     INPUT CHANGE
  ============================ */

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  /* ============================
     IMAGE HANDLING
  ============================ */

  const handleAddSelectedImages = () => {
    if (selectedDatabaseImages.length === 0) return;

    setFormData((prev) => ({
      ...prev,
      featured_image: [
        ...prev.featured_image,
        ...selectedDatabaseImages.filter(
          (url) => !prev.featured_image.includes(url)
        ),
      ],
    }));

    setSelectedDatabaseImages([]);
    setShowImageDropdown(false);
  };

  const removeUploadedImage = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      featured_image: prev.featured_image.filter(
        (_, index) => index !== indexToRemove
      ),
    }));
  };

  /* ============================
     CREATE BLOG
  ============================ */

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const payload = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        meta_title: formData.meta_title || "",
        meta_description: formData.meta_description || "",
        status: formData.status || "DRAFT",
        featured_image: formData.featured_image,
      };

      console.log("Sending blog payload:", payload);

      await createBlog(payload);
      await fetchBlogs();
      resetForm();
    } catch (err) {
      console.error("Create blog failed:", err);
    }
  };

  /* ============================
     TOGGLE PUBLISH
  ============================ */

  const togglePublish = async (id) => {
    try {
      const post = posts.find((p) => p.id === id);

      const payload = {
        status: post.status === "PUBLISHED" ? "DRAFT" : "PUBLISHED",
      };

      await updateBlog(id, payload);
      fetchBlogs();
    } catch (err) {
      console.error("Update failed", err);
    }
  };

  return (
    <div className="blog-page">
      <section className="blog-hero">
        <div>
          <h1>Blog Management</h1>
          <p>
            Create/edit posts, publish/unpublish, and manage SEO + featured
            image.
          </p>
        </div>
        <div className="hero-meta">{summaryLabel}</div>
      </section>

      <section className="blog-shell">

        <article className="blog-form-card">

          <div className="card-top-row">
            <h2>Create Blog Post</h2>
            <button type="button" className="outline-btn" onClick={resetForm}>
              Clear Form
            </button>
          </div>

          <div className="form-field">
            <label className="form-label">
              Title <span className="required-mark">*</span>
            </label>
            <input
              className="blog-field-input"
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter blog title..."
            />
            {errors.title && <p className="error-text">{errors.title}</p>}
          </div>

          <div className="form-field">
            <label className="form-label">
              Content <span className="required-mark">*</span>
            </label>
            <textarea
              className="blog-field-textarea"
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              placeholder="Write your blog content here..."
            />
            {errors.content && <p className="error-text">{errors.content}</p>}
          </div>

          <div className="form-field">
            <label className="form-label">Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
            >
              <option value="DRAFT">DRAFT</option>
              <option value="PUBLISHED">PUBLISHED</option>
            </select>
            {errors.status && <p className="error-text">{errors.status}</p>}
          </div>

          <div className="form-field">
            <label className="form-label">Meta Title</label>
            <input
              type="text"
              name="meta_title"
              value={formData.meta_title}
              onChange={handleInputChange}
              placeholder="SEO Title"
            />
            {errors.meta_title && (
              <p className="error-text">{errors.meta_title}</p>
            )}
          </div>

          <div className="form-field">
            <label className="form-label">Meta Description</label>
            <input
              type="text"
              name="meta_description"
              value={formData.meta_description}
              onChange={handleInputChange}
              placeholder="SEO description"
            />
            {errors.meta_description && (
              <p className="error-text">{errors.meta_description}</p>
            )}
          </div>

          <div className="form-field">
            <label className="form-label">Featured Image</label>
            <div className="image-action-row">
              <button
                type="button"
                className="outline-btn"
                onClick={() => navigate(`${adminBasePath}/admin-images`)}
              >
                Upload
              </button>

              <button
                type="button"
                className="outline-btn"
                onClick={fetchImages}
              >
                Choose Image
              </button>

              <button
                type="button"
                className="outline-btn"
                onClick={handleAddSelectedImages}
              >
                Add
              </button>
            </div>

            {errors.featured_image && (
              <p className="error-text">{errors.featured_image}</p>
            )}
          </div>

          {showImageDropdown && (
            <select
              value={selectedDatabaseImages}
              onChange={handleDatabaseImageSelect}
              multiple
            >
              {availableImages.map((image) => (
                <option key={image.id} value={image.url}>
                  {image.name}
                </option>
              ))}
            </select>
          )}

          {formData.featured_image?.length > 0 && (
            <div className="preview-section">
              <p className="preview-caption">
                Selected Images: {formData.featured_image.length}
              </p>

              <div className="preview-image-grid">
                {formData.featured_image.map((url, index) => (
                  <div key={index} className="preview-image-tile">
                    {isVideoUrl(url) ? (
                      <video
                        className="preview-video"
                        controls
                        preload="metadata"
                      >
                        <source src={url} type={getVideoMimeType(url)} />
                      </video>
                    ) : (
                      <img
                        className="preview-image"
                        src={url}
                        alt="Blog preview"
                      />
                    )}

                    <button
                      type="button"
                      className="preview-remove-btn"
                      onClick={() => removeUploadedImage(index)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="action-row">
            <button className="primary-btn" onClick={handleSave}>
              Create Post
            </button>
          </div>

        </article>

        {/* ================= POSTS ================= */}

        <article className="blog-list-card">

          <div className="list-head-row">

            <h2
              role="button"
              tabIndex={0}
              className="all-posts-link-title"
              onClick={() => navigate("/pages/BlogPost")}
            >
              All Posts
            </h2>

            <button
              type="button"
              className="outline-btn"
              onClick={() => navigate(`${adminBasePath}/blog-post`)}
            >
              Click to All Posts
            </button>

          </div>

          <div className="post-list">

            {posts.map((post) => (
              <div className="post-card" key={post.id}>

                <h3>{post.title}</h3>

                <span
                  className={`status-pill ${
                    post.status === "PUBLISHED" ? "live" : "draft"
                  }`}
                >
                  {post.status}
                </span>

                <p>SEO: {post.meta_title || "Not set"}</p>

                <p>Meta: {post.meta_description || "Not set"}</p>

                <p>Updated: {post.updatedAt}</p>

                <button
                  onClick={() => togglePublish(post.id)}
                  className="publish-btn"
                >
                  {post.status === "PUBLISHED"
                    ? "Unpublish"
                    : "Publish"}
                </button>

              </div>
            ))}

            {posts.length === 0 && (
              <div className="empty-box">
                No blog posts available. Create your first post.
              </div>
            )}

          </div>

        </article>

      </section>
    </div>
  );
};

export default BlogManagement;
